
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { fetchResource } from 'api-provider';
import DashboardTabbedSection from 'containers/Dashboard/components/dashboardSection/DashboardTabbedSection';
import DashboardSectionTab from 'containers/Dashboard/components/dashboardSection/DashboardSectionTab';
import { PermissionsContext } from 'contexts/permissionsContext';
import __ from 'utils/i18n';
import { MovementsTable } from './components/MovementsTable';
import { MovementsFilters } from './components/MovementsFilters';
import { MovementsExport } from './components/MovementsExport';
import { getMovements } from './_shared/resources';
import { DEFAULT_PAGE_SIZE, MAX_DAYS_VIEW, MAX_DAYS_DOWNLOAD } from './constants';
import { calculateDaysDifference } from './_shared/dateValidations';

const MovementsTabContent = () => {
  const token = useSelector((state) => state.api?.token);
  const api = useSelector((state) => state.api);
  const { merchantId: digitalAccountId } = useSelector((state) => state.login?.user || {});

  const [filters, setFilters] = useState({
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  });
  const [page, setPage] = useState(1);
  const [allData, setAllData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  // Función para obtener movimientos
  const fetchMovements = useCallback((pageNum, resetData = false) => {
    if (!digitalAccountId || !api) return;

    setIsFetching(true);
    setError(null);

    const resource = getMovements(digitalAccountId, token)({
      ...filters,
      page: pageNum,
      pageSize: DEFAULT_PAGE_SIZE,
    });

    fetchResource(resource, {}, api)
      .onSuccess((response) => {
        if (response && response.data) {
          const { data, meta } = response.data;

          if (resetData) {
            setAllData(data || []);
          } else {
            setAllData((prev) => [...prev, ...(data || [])]);
          }

          setHasMore(meta?.hasMore || false);
          setPage(pageNum);
        }
        setIsFetching(false);
      })
      .onFailure((err) => {
        console.error('Error fetching movements:', err);
        
        // Manejar errores específicos del backend
        const errorData = err?.response?.data || err?.data || {};
        let errorMessage = 'Error al cargar los movimientos';
        
        if (errorData.code === 'REQUIRES_EXPORT') {
          errorMessage = errorData.message || __('dashboard.sections.movements.requiresExport');
        } else if (errorData.code === 'MAX_DAYS_EXCEEDED') {
          errorMessage = errorData.message || __('dashboard.sections.movements.maxDaysExceeded');
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setIsFetching(false);
      });
  }, [digitalAccountId, token, filters, api]);

  // Cargar datos iniciales
  useEffect(() => {
    if (digitalAccountId && api) {
      const daysDiff = calculateDaysDifference(filters.startDate, filters.endDate);
      
      // Solo hacer petición si es ≤ 30 días
      if (daysDiff <= MAX_DAYS_VIEW) {
        fetchMovements(1, true);
      } else {
        // Limpiar datos para rangos > 30 días
        setAllData([]);
        setError(null);
        setIsFetching(false);
      }
    }
  }, [digitalAccountId, api, filters, fetchMovements]);

  // Manejar cambio de filtros
  const handleFilter = useCallback((newFilters) => {
    setFilters(newFilters);
    setPage(1);
    setAllData([]);
    setHasMore(true);
  }, []);

  // Manejar carga de más datos (lazy loading)
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      fetchMovements(page + 1, false);
    }
  }, [fetchMovements, page, hasMore, isFetching]);

  if (!digitalAccountId) {
    return (
      <div className="movements-no-account">
        <p>{__('dashboard.sections.movements.noDigitalAccount')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-card">
      <div className="dashboard-card-header">
        <MovementsFilters
          onFilter={handleFilter}
          initialStartDate={filters.startDate}
          initialEndDate={filters.endDate}
        >
          <MovementsExport
            digitalAccountId={digitalAccountId}
            token={token}
            currentStartDate={filters.startDate}
            currentEndDate={filters.endDate}
            disabled={calculateDaysDifference(filters.startDate, filters.endDate) > MAX_DAYS_DOWNLOAD}
          />
        </MovementsFilters>
      </div>
      <div className="dashboard-card-body">
        {(() => {
          const daysDiff = calculateDaysDifference(filters.startDate, filters.endDate);
          const shouldShowTable = daysDiff <= MAX_DAYS_VIEW;
          const isValidRange = daysDiff <= MAX_DAYS_DOWNLOAD;

          return (
            <>
              {/* Mensaje para rango 31-90 días */}
              {!shouldShowTable && isValidRange && (
                <div className="dashboard-card-message">
                  <i className="mdi mdi-information" />
                  <h3>{__('dashboard.sections.movements.requiresDownload', 'Utilice la función de descarga')}</h3>
                  <p>
                    {__('dashboard.sections.movements.requiresDownloadMessage', 
                      `Para consultas superiores a ${MAX_DAYS_VIEW} días, debe utilizar la función de descarga.`
                    )}
                  </p>
                </div>
              )}

              {/* Error para > 90 días */}
              {!isValidRange && (
                <div className="dashboard-card-message">
                  <i className="mdi mdi-alert-circle" />
                  <h3>{__('dashboard.sections.movements.error')}</h3>
                  <p>
                    {__('dashboard.sections.movements.maxDaysExceeded', 
                      `El rango seleccionado (${daysDiff} días) supera el máximo permitido de ${MAX_DAYS_DOWNLOAD} días.`
                    )}
                  </p>
                </div>
              )}

              {/* Error de API (solo para ≤ 30 días) */}
              {error && shouldShowTable && (
                <div className="dashboard-card-message">
                  <i className="mdi mdi-alert-circle" />
                  <h3>{__('dashboard.sections.movements.error')}</h3>
                  <p>{error}</p>
                </div>
              )}

              {/* Mensaje de no data (solo para ≤ 30 días) */}
              {!allData.length && !isFetching && !error && shouldShowTable && (
                <div className="dashboard-card-message">
                  <i className="mdi mdi-minus-circle" />
                  <h3>{__('dashboard.sections.movements.noMovements')}</h3>
                  <p>{__('dashboard.sections.movements.noMovementsMessage')}</p>
                </div>
              )}

              {/* Tabla (solo para ≤ 30 días con data) */}
              {allData.length > 0 && !error && shouldShowTable && (
                <MovementsTable
                  data={allData}
                  isFetching={isFetching}
                  hasMore={hasMore}
                  getMore={handleLoadMore}
                />
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

const MovementsComponent = () => {
  const { userPermissions, featureFlags } = useContext(PermissionsContext);

  const isMovementsEnabled =
    featureFlags?.includes('movements_main') &&
    userPermissions?.includes('movements_main');


  if (!isMovementsEnabled) {
    return null;
  }

  return (
    <DashboardTabbedSection>
      <DashboardSectionTab
        path="movements"
        title={__('dashboard.sections.movements.tabs.movements')}
      >
        <MovementsTabContent />
      </DashboardSectionTab>
    </DashboardTabbedSection>
  );
};

export const Movements = MovementsComponent;
export default Movements;
